import { useState } from "react";
import type { FormEvent } from "react";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  useAceitarSolucao,
  useAvaliarAtendimento,
  useCiclo,
  useReabrirChamado,
} from "../../nucleo/tickets-operacao";
import type { EstadoDoTicket } from "../../nucleo/tickets";
import { Aviso, Botao, Cartao, CampoLongo } from "../ui";

interface Props {
  ticketId: string;
  estado: EstadoDoTicket;
  /** `ticket:fechar_ciclo`: quem pode aceitar, reabrir e avaliar (o cliente, nunca a equipe). */
  podeFecharCiclo: boolean;
}

function vezes(total: number): string {
  return total === 1 ? "1 vez" : `${total} vezes`;
}

/** Aceitar a solução, reabrir com motivo e avaliar o atendimento; a equipe lê o resultado. */
export function FechamentoDoCiclo({ ticketId, estado, podeFecharCiclo }: Props) {
  const ciclo = useCiclo(ticketId);
  const aceitar = useAceitarSolucao(ticketId);
  const reabrir = useReabrirChamado(ticketId);
  const avaliar = useAvaliarAtendimento(ticketId);
  const [reabrindo, setReabrindo] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [nota, setNota] = useState(0);
  const [comentario, setComentario] = useState("");

  if (ciclo.isError) return <Aviso tom="erro" className="mb-4">{mensagemDoConsole(ciclo.error)}</Aviso>;
  if (!ciclo.data) return null;
  const { reaberturas, avaliacao } = ciclo.data;

  const podeAceitar = podeFecharCiclo && estado === "resolvido";
  const podeReabrir = podeFecharCiclo && (estado === "resolvido" || estado === "fechado");
  const podeAvaliar = podeFecharCiclo && estado === "fechado" && !avaliacao;
  if (!podeReabrir && !podeAvaliar && !avaliacao && reaberturas === 0) return null;

  function confirmarReabertura(evento: FormEvent) {
    evento.preventDefault();
    reabrir.mutate(motivo.trim(), {
      onSuccess: () => {
        setReabrindo(false);
        setMotivo("");
      },
    });
  }

  function enviarAvaliacao(evento: FormEvent) {
    evento.preventDefault();
    avaliar.mutate({ nota, comentario: comentario.trim() || null });
  }

  return (
    <Cartao
      titulo={podeAceitar ? "A solução resolveu o seu problema?" : "Andamento do atendimento"}
      className="mb-4"
      data-testid="ciclo-do-chamado"
    >
      {reaberturas > 0 ? (
        <p className="nota-rodape mb-3" data-testid="reaberturas-do-chamado">
          Este chamado já foi reaberto {vezes(reaberturas)}.
        </p>
      ) : null}

      {podeAceitar ? (
        <p className="cartao__texto mb-3">
          A equipe marcou o chamado como resolvido. Aceite a solução para fechá-lo ou reabra se o problema continua.
        </p>
      ) : null}
      {podeAceitar || podeReabrir ? (
        <div className="flex flex-wrap gap-2">
          {podeAceitar ? (
            <Botao onClick={() => aceitar.mutate()} disabled={aceitar.isPending}>
              {aceitar.isPending ? "Fechando…" : "Aceitar solução"}
            </Botao>
          ) : null}
          {podeReabrir && !reabrindo ? (
            <Botao variante="fantasma" onClick={() => setReabrindo(true)}>
              Reabrir chamado
            </Botao>
          ) : null}
        </div>
      ) : null}
      {aceitar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(aceitar.error)}</Aviso> : null}

      {reabrindo ? (
        <form className="mt-3" onSubmit={confirmarReabertura} data-testid="reabrir-chamado">
          <CampoLongo
            rotulo="Motivo da reabertura"
            ajuda="Conte o que continua acontecendo. A equipe lê este texto na conversa."
            value={motivo}
            onChange={(evento) => setMotivo(evento.target.value)}
            required
          />
          <div className="flex gap-2">
            <Botao type="submit" disabled={motivo.trim().length < 3 || reabrir.isPending}>
              {reabrir.isPending ? "Reabrindo…" : "Confirmar reabertura"}
            </Botao>
            <Botao variante="fantasma" onClick={() => setReabrindo(false)}>
              Cancelar
            </Botao>
          </div>
        </form>
      ) : null}
      {reabrir.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(reabrir.error)}</Aviso> : null}

      {podeAvaliar ? (
        <form className="mt-4 border-t border-borda pt-4" onSubmit={enviarAvaliacao} data-testid="avaliar-atendimento">
          <fieldset className="campo">
            <legend className="campo__rotulo">Como foi o atendimento?</legend>
            <div className="flex flex-wrap gap-3 pt-1" role="radiogroup" aria-label="Nota do atendimento">
              {[1, 2, 3, 4, 5].map((valor) => (
                <label className="flex items-center gap-1" key={valor}>
                  <input
                    type="radio"
                    name={`nota-${ticketId}`}
                    value={valor}
                    checked={nota === valor}
                    onChange={() => setNota(valor)}
                  />
                  {valor}
                </label>
              ))}
            </div>
            <p className="campo__ajuda">1 é muito ruim e 5 é excelente.</p>
          </fieldset>
          <CampoLongo
            rotulo="Comentário (opcional)"
            value={comentario}
            onChange={(evento) => setComentario(evento.target.value)}
            maxLength={2000}
          />
          <Botao type="submit" disabled={nota === 0 || avaliar.isPending}>
            {avaliar.isPending ? "Enviando…" : "Enviar avaliação"}
          </Botao>
        </form>
      ) : null}
      {avaliar.isError ? <Aviso tom="erro" className="mt-3">{mensagemDoConsole(avaliar.error)}</Aviso> : null}

      {avaliacao ? (
        <div className={podeReabrir || podeAvaliar || reaberturas > 0 ? "mt-4 border-t border-borda pt-4" : ""} data-testid="avaliacao-do-chamado">
          <strong>Avaliação: {avaliacao.nota} de 5</strong>
          {avaliacao.comentario ? <p className="texto-preservado mt-1">{avaliacao.comentario}</p> : null}
        </div>
      ) : null}
    </Cartao>
  );
}
